// Import Vuetify CSS first so Tailwind utilities can extend it
import 'vuetify/styles';
import './assets/styles/index.css';
import './assets/styles/simple-keyboard.css';

import convertColor from 'color-convert';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import mitt from 'mitt';
import NProgress from 'nprogress';
import { createApp } from 'vue';
import { configureCompat } from '@vue/compat';
import { createVuetify } from 'vuetify';
import { en } from 'vuetify/locale';

import App from './App.vue';
import router from './router';
import ApiService from './services/api.service';
import { StorageService } from './services/storage.service';
import store from './store';
import { datadogRum } from '@datadog/browser-rum-slim';
import { customMdiSvg, aliases as mdiAliases } from './plugins/icons';

// Enable Vue 3 compatibility mode for migration
configureCompat({
  MODE: 2,
  GLOBAL_MOUNT_CONTAINER: false,
});

// Configure dayjs plugins
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(minMax);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);

// Initialize API service
ApiService.init(process.env.VUE_APP_API_BASE_URL);
ApiService.setHeaders();

// Create event bus using mitt
const eventBus = mitt();

// Create Vue 3 app
const app = createApp(App);

// Add global properties
app.config.globalProperties.$dayjs = dayjs;
app.config.globalProperties.$progress = NProgress;
app.config.globalProperties.$eventBus = eventBus;
app.config.globalProperties.$api = ApiService;
app.config.globalProperties.$storage = StorageService;

// Add global filters as properties
app.config.globalProperties.$filters = {
  initials(value: string): string {
    if (!value) { return ''; }
    return value.split(' ').map((n: string) => n[0]).join('');
  },

  foregroundColor(value: string): string {
    if (!value || typeof value !== 'string') { return 'black'; }
    const [, , lightness] = convertColor.hex.hsl(value.replace('#', ''));
    return lightness > 50
      ? lightness > 80 ? '#404040' : 'black'
      : 'white';
  },

  timestampString(value: number = 0): string {
    const isLongerThanOneHour = value >= 3600000;
    return `${
      dayjs.utc(value).format(isLongerThanOneHour ? 'hh:mm' : 'mm:ss')
    }${
      isLongerThanOneHour ? 'm' : 's'
    }`;
  },
};

datadogRum.init({
  clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN,
  applicationId: process.env.VUE_APP_DATADOG_APPLICATION_ID,
  site: process.env.VUE_APP_DATADOG_SITE,
  service: process.env.VUE_APP_DATADOG_SERVICE_NAME,
  env: process.env.VUE_APP_ENV,
  version: process.env.VUE_APP_VERSION,
  trackResources: true,
  trackLongTasks: true,
  trackUserInteractions: true,
  trackViewsManually: true,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
});

const vuetify = createVuetify({
  icons: {
    defaultSet: 'mdi',
    aliases: mdiAliases,
    sets: { mdi: customMdiSvg },
  },
  locale: {
    locale: 'en',
    messages: { en },
  },
  defaults: {
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        dark: false,
        variables: {}, // Required for Vuetify 3
        colors: {
          'primary': '#086397',
          'red': '#E83123',
          'red-darken-2': '#C62828',
          'yellow': '#FBE04B',
          'green': '#A0FCD1',
          'green-darken-1': '#72B599',
          'green-darken-2': '#388E3C',
          'blue': '#E3F9FF',
          'blue-lighten-2': '#E3F9FF',
          'blue-darken-2': '#4E61B1',
          'purple': '#B549EF',
          'gray': '#7F7F7F',
          'gray-darken-3': '#E6E5E5',
          'gray-darken-2': '#404040',
          'gray-darken-1': '#F4F4F4',
          'gray-lighten-1': '#FAFAFA',
          'gray-lighten-2': '#F5F6F7',
          'gray-lighten-3': '#ADADAD',
          'gray-tile': '#EAEAEA',
          'gray-primary': '#404040',
          'gray-secondary': '#707070',
          // Grey aliases (British spelling used in components)
          'grey': '#7F7F7F',
          'grey-darken-4': '#212121',
          'grey-darken-3': '#424242',
          'grey-darken-2': '#616161',
          'grey-darken-1': '#757575',
          'grey-lighten-1': '#FAFAFA',
          'grey-lighten-2': '#F5F6F7',
          'grey-lighten-3': '#EEEEEE',
          'grey-lighten-4': '#F5F5F5',
        },
      },
    },
  },
});

// Use Vue 3 plugins
app.use(store);
app.use(router);
app.use(vuetify);

// Mount the app
app.mount('#app');
